import React, { useEffect, useState } from 'react';
import { Smartphone, Download, X } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Install App button — single entry point that works across all platforms.
 *
 *  - Android / Chrome / Edge / Brave: hooks the `beforeinstallprompt` event
 *    and triggers the native A2HS prompt on click.
 *  - iOS Safari: no native prompt; shows a styled instruction sheet
 *    ("Tap Share → Add to Home Screen").
 *  - Already-installed (display-mode: standalone): hides itself.
 *  - Desktop browsers without prompt: still shows the iOS-style instruction
 *    fallback ("Use your browser's install / Add to Home Screen action").
 */

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  // iPadOS 13+ reports as Mac in user agent — also check touch points
  const ua = navigator.userAgent || '';
  return (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  );
};

const InstallAppButton = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showIosSheet, setShowIosSheet] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onBeforeInstall = (e) => {
      // Stash for our custom UI
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setInstalled(true);
      }
      setDeferredPrompt(null);
      return;
    }
    // No native prompt available — show iOS / desktop fallback sheet
    setShowIosSheet(true);
  };

  if (installed) return null;

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outline"
        size="sm"
        className="border-[#14F195]/60 text-[#14F195] hover:bg-[#14F195]/10 hover:text-[#14F195] gap-2 font-game text-[10px]"
        data-testid="install-app-btn"
      >
        <Download className="w-3.5 h-3.5" />
        INSTALL APP
      </Button>

      {showIosSheet && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
          onClick={() => setShowIosSheet(false)}
          data-testid="install-instructions-overlay"
        >
          <div
            className="w-full max-w-md bg-[#0f0f0f] border-t-2 sm:border-2 border-[#14F195] sm:rounded-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
            data-testid="install-instructions-sheet"
          >
            <button
              onClick={() => setShowIosSheet(false)}
              className="absolute top-3 right-3 p-1 text-[#A1A1AA] hover:text-white transition-colors"
              data-testid="install-instructions-close"
              aria-label="Close install instructions"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-[#14F195]/15 border border-[#14F195]/40">
                <Smartphone className="w-5 h-5 text-[#14F195]" />
              </div>
              <h3 className="font-game text-sm text-white tracking-wide">INSTALL METAL LEGO</h3>
            </div>

            {isIOS() ? (
              <div className="space-y-4 text-sm text-[#E0E0E0]">
                <p className="text-[#A1A1AA]">
                  iOS Safari doesn't auto-install web apps — but you can add this game to your
                  home screen in two taps.
                </p>
                <ol className="space-y-3 list-decimal list-inside font-game text-[10px] leading-relaxed">
                  <li>
                    Tap the <span className="text-[#14F195]">Share</span> button at the bottom of Safari
                    (the square with the up-arrow).
                  </li>
                  <li>
                    Scroll down and tap <span className="text-[#14F195]">"Add to Home Screen"</span>.
                  </li>
                  <li>
                    Tap <span className="text-[#14F195]">Add</span> in the top-right corner.
                  </li>
                </ol>
                <p className="text-xs text-[#A1A1AA] pt-2 border-t border-[#222]">
                  Tip: Make sure you're using <span className="text-white">Safari</span> — Chrome on iOS
                  cannot install web apps.
                </p>
              </div>
            ) : (
              <div className="space-y-4 text-sm text-[#E0E0E0]">
                <p className="text-[#A1A1AA]">
                  Your browser doesn't support one-tap install. You can still add Metal Lego to
                  your device:
                </p>
                <ul className="space-y-2 font-game text-[10px] leading-relaxed">
                  <li>
                    <span className="text-[#14F195]">Android Chrome</span>: tap the ⋮ menu →
                    <span className="text-white"> "Add to Home screen"</span>.
                  </li>
                  <li>
                    <span className="text-[#14F195]">Desktop Chrome / Edge</span>: click the install
                    icon in the address bar (right side).
                  </li>
                  <li>
                    <span className="text-[#14F195]">Firefox</span>: open the menu →
                    <span className="text-white"> "Install"</span> (if available for this site).
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default InstallAppButton;
